/**
 * groupSlice — CRUD de grupos de dados para organização de referências no projeto
 *
 * Consumidores: GrupoPage (via driveProjectStore)
 * Dependências: googleDrive, helpers
 * Persistência: Google Drive (groups.json por projeto)
 */
import { googleDrive } from '../../services/googleDrive';
import { ensureAuthenticated } from './helpers';
import { logger } from '../../utils/logger';
import { driveErrorToast } from '../../utils/driveErrorToast';
import { auditLog } from '../../utils/auditLog';
import type { DataGroup, SliceSet, SliceGet } from './types';

export const createGroupSlice = (set: SliceSet, get: SliceGet) => {

  // Timestamp da última mutação local (optimistic update).
  // Usado pelo _backgroundRevalidateGroups para evitar sobrescrever dado local
  // mais recente com dado stale vindo do Drive.
  let _lastMutationAt = 0;

  // Closure boolean para guard de revalidação (não no store para evitar re-renders)
  let _isRevalidatingGroups = false;

  // Background revalidation para grupos
  const _backgroundRevalidateGroups = (projectId: string) => {
    if (_isRevalidatingGroups) return;
    _isRevalidatingGroups = true;

    const startedAt = Date.now();

    ensureAuthenticated().then(() =>
      googleDrive.getProjectGroups(projectId)
    ).then(freshGroups => {
      _isRevalidatingGroups = false;

      // Guard: só atualiza se o projeto ativo não mudou durante o fetch
      if (get().activeProjectId !== projectId) return;

      // Guard: se houve mutação local APÓS o início desta revalidação,
      // descartar a resposta do Drive para não sobrescrever dado mais recente.
      if (_lastMutationAt > startedAt) {
        logger.info('groupSlice', 'Revalidação groups descartada — mutação local mais recente');
        return;
      }

      if (freshGroups !== null) {
        const sortedGroups = (freshGroups || []).sort((a: DataGroup, b: DataGroup) => a.order - b.order);
        set({ cachedGroups: sortedGroups, groupsLoaded: true, groupsLoadedAt: Date.now() });
      }
    }).catch((err) => {
      _isRevalidatingGroups = false;
      logger.error('[groupSlice] Revalidação groups em background falhou:', String(err));
    });
  };

  return {
  // ============================================
  // FUNÇÕES DE GRUPOS DE DADOS (FASE 3)
  // ============================================

  // Carrega grupos do projeto ativo
  loadActiveProjectGroups: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      set({ cachedGroups: [], groupsLoaded: true });
      return [];
    }

    // Se já carregou E dados são frescos (< 5 min), retorna do Zustand sem fetch
    const groupsLoadedAt = get().groupsLoadedAt;
    if (get().groupsLoaded && groupsLoadedAt && Date.now() - groupsLoadedAt < 300000) {
      return get().cachedGroups || [];
    }

    // Se já carregou MAS dados estão stale (≥ 5 min): serve cache instantâneo + revalida em background
    if (get().groupsLoaded) {
      _backgroundRevalidateGroups(activeProjectId);
      return get().cachedGroups || [];
    }

    try {
      await ensureAuthenticated();
      const groups = await googleDrive.getProjectGroups(activeProjectId);
      // Ordenar por order
      const sortedGroups = (groups || []).sort((a: DataGroup, b: DataGroup) => a.order - b.order);
      set({ cachedGroups: sortedGroups, groupsLoaded: true, groupsLoadedAt: Date.now() });
      return sortedGroups;
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao carregar grupos:', error);
      set({ cachedGroups: [], groupsLoaded: true, groupsLoadedAt: Date.now() });
      return [];
    }
  },

  // Cria grupo no projeto ativo
  createGroupInActiveProject: async (name: string, color?: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      const newGroupId = await googleDrive.addGroupToProject(activeProjectId, name, color);

      // Drive confirmou — atualiza cache localmente
      const newGroup: DataGroup = {
        id: newGroupId,
        name,
        color: color || '#477B72',
        order: (get().cachedGroups || []).length,
        createdAt: new Date().toISOString()
      };

      set(state => ({
        cachedGroups: [...state.cachedGroups, newGroup],
        groupsLoadedAt: Date.now(),
      }));

      auditLog({ operation: 'create', target: 'grupo', targetId: newGroupId, detail: `Grupo criado: ${name}`, success: true });
      return newGroupId;
    } catch (error) {
      logger.error('[groupSlice.createGroup]', error);
      driveErrorToast(error, 'Não foi possível criar o grupo. Tente novamente.');
      auditLog({ operation: 'create', target: 'grupo', detail: `Falha ao criar grupo: ${name}`, success: false, error: String(error) });
      return '';
    }
  },

  // Atualiza grupo no projeto ativo
  updateGroupInActiveProject: async (groupId: string, updates: {
    name?: string;
    color?: string;
    order?: number;
  }) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectGroup(activeProjectId, groupId, updates);

      // Drive confirmou — atualiza cache localmente
      set(state => ({
        cachedGroups: (state.cachedGroups || []).map(g =>
          g.id === groupId ? { ...g, ...updates } : g
        ),
        groupsLoadedAt: Date.now(),
      }));
      auditLog({ operation: 'update', target: 'grupo', targetId: groupId, detail: `Grupo atualizado: ${Object.keys(updates).join(', ')}`, success: true });
    } catch (error) {
      logger.error('[groupSlice.updateGroup]', error);
      driveErrorToast(error, 'Não foi possível atualizar o grupo. Tente novamente.');
      auditLog({ operation: 'update', target: 'grupo', targetId: groupId, detail: `Falha ao atualizar grupo`, success: false, error: String(error) });
    }
  },

  // Remove grupo do projeto ativo
  deleteGroupFromActiveProject: async (groupId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      await googleDrive.deleteProjectGroup(activeProjectId, groupId);

      // Drive confirmou — atualiza cache localmente
      set(state => ({
        cachedGroups: state.cachedGroups
          .filter(g => g.id !== groupId)
          .map((g, index) => ({ ...g, order: index })),
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.groupId === groupId ? { ...d, groupId: null } : d
        ),
        groupsLoadedAt: Date.now(),
      }));
      auditLog({ operation: 'delete', target: 'grupo', targetId: groupId, detail: `Grupo removido`, success: true });
    } catch (error) {
      logger.error('[groupSlice.deleteGroup]', error);
      driveErrorToast(error, 'Não foi possível remover o grupo. Tente novamente.');
      auditLog({ operation: 'delete', target: 'grupo', targetId: groupId, detail: `Falha ao remover grupo`, success: false, error: String(error) });
    }
  },

  // Reordena grupos no projeto ativo
  reorderGroupsInActiveProject: async (groupIds: string[]) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      await googleDrive.reorderProjectGroups(activeProjectId, groupIds);

      // Drive confirmou — atualiza cache localmente
      set(state => ({
        cachedGroups: groupIds.map((id, index) => {
          const group = state.cachedGroups.find(g => g.id === id);
          return group ? { ...group, order: index } : null;
        }).filter(Boolean) as DataGroup[],
        groupsLoadedAt: Date.now(),
      }));
      auditLog({ operation: 'update', target: 'grupo', detail: `Grupos reordenados`, success: true });
    } catch (error) {
      logger.error('[groupSlice.reorderGroups]', error);
      driveErrorToast(error, 'Não foi possível reordenar os grupos. Tente novamente.');
      auditLog({ operation: 'update', target: 'grupo', detail: `Falha ao reordenar grupos`, success: false, error: String(error) });
    }
  },

  // Associa dado a um grupo (ou remove associação com null)
  assignDataToGroup: async (dataId: string, groupId: string | null) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { groupId });

      // Drive confirmou — atualiza cache localmente
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, groupId } : d
        ),
        groupsLoadedAt: Date.now(),
      }));
      auditLog({ operation: 'update', target: 'grupo', targetId: groupId || 'none', detail: `Dado ${dataId} ${groupId ? 'associado ao' : 'removido do'} grupo`, success: true });
    } catch (error) {
      logger.error('[groupSlice.assignDataToGroup]', error);
      driveErrorToast(error, 'Não foi possível associar o dado ao grupo. Tente novamente.');
      auditLog({ operation: 'update', target: 'grupo', targetId: groupId || 'none', detail: `Falha ao associar dado ${dataId} ao grupo`, success: false, error: String(error) });
    }
  },

  // Retorna grupos do cache (síncrono)
  getCachedGroups: () => {
    return get().cachedGroups || [];
  },

  // Invalida o cache de grupos
  invalidateGroupsCache: () => {
    set({ cachedGroups: [], groupsLoaded: false, groupsLoadedAt: null });
  },

  // Revalida grupos em background SE dados estão stale (≥ 5 min).
  // Chamado pelo visibilitychange do DashboardLayout para sync cross-device.
  revalidateGroupsIfStale: () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) return;
    if (!get().groupsLoaded) return;

    const loadedAt = get().groupsLoadedAt;
    if (loadedAt && Date.now() - loadedAt < 300000) return;

    _backgroundRevalidateGroups(activeProjectId);
  },
};
};
